/**
 * Which HTTP statuses the edge-resolve retry ladder must NOT retry.
 *
 * 2026-09-07: `/v1/api/servers/resolve` answers 406 for an unsupported file type (and, until
 * the professorx fix of the same day, for any file name containing a comma). The ladder retried
 * it five times with exponential backoff, so every such user waited ~31 s for the same answer.
 * A 4xx is a verdict about the request, not a transient fault — except 408 (request timeout)
 * and 429 (rate limit), which are exactly the transient ones.
 */
export function isTerminalResolveStatus(status: unknown): boolean {
    if (typeof status !== 'number' || !Number.isFinite(status)) return false;
    if (status < 400 || status >= 500) return false;
    return status !== 408 && status !== 429;
}

/**
 * Code 1 (ENFORCEMENT-POLICY.md §6.2 item 1): professorx answers a PLAIN `HTTP 429` with `Retry-After`
 * and `{code:'rate_paced', retryAfterSeconds}` — no `dataType` — on `/servers/resolve` as well as on
 * `/tasks/schedule`. That is pacing, not a denial: the resolve waits and asks again. A 429 that
 * carries the enforcement envelope (`dataType: 'enforcement_error'`) or a 402 `insufficient_credits`
 * is the denial and is never retried (`EnforcementErrorService.isEnforcementError`).
 */
export function isPacedResolveError(e: unknown): boolean {
    if (!e || typeof e !== 'object') return false;
    if ((e as { status?: unknown }).status !== 429) return false;
    const body = (e as { response?: unknown }).response;
    if (body && typeof body === 'object') {
        const b = body as { dataType?: unknown; code?: unknown };
        if (b.dataType === 'enforcement_error' || b.code === 'insufficient_credits') return false;
    }
    return true;
}

/** What the resolver tells its caller while a resolve is paced (null = no longer paced). */
export interface ResolvePacingState {
    /** 1-based number of the attempt about to be made. */
    attempt: number;
    maxAttempts: number;
    delayMs: number;
    fromRetryAfter: boolean;
}

/** Tag on the error the resolver throws when the 5-minute pacing budget is spent. */
export const RESOLVE_PACED_TAG = 'resolvePaced';

export function isResolvePacedGiveUp(e: unknown): boolean {
    return !!e && typeof e === 'object' && (e as Record<string, unknown>)[RESOLVE_PACED_TAG] === true;
}

/** The staged row's line while its resolve is paced — the same words as the download card's queued state. */
export function resolvePacedText(state: ResolvePacingState): string {
    return state.attempt > 1
        ? `Queued — server busy, retrying (${state.attempt - 1} of ${state.maxAttempts})`
        : 'Queued — server busy, retrying';
}

export const RESOLVE_PACED_GIVE_UP_COPY = 'The server is pacing new conversions right now. Your file is still here — press Retry in a moment.';
